import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, MaskedLines, Eyebrow } from "@/components/Reveal";
import ProfileCard from "@/components/ProfileCard";
import { PROFILES } from "@/config";

const SPREAD = 248;

function FanCard({ p, i, progress }) {
  const reduce = useReducedMotion();
  const off = i - 2;
  const x = useTransform(progress, [0, 1], [off * 14, off * SPREAD]);
  const rotate = useTransform(progress, [0, 1], [off * 1.5, off * 5]);
  const y = useTransform(progress, [0, 1], [0, Math.abs(off) * 16]);

  if (reduce) {
    return (
      <div
        className="absolute"
        style={{ transform: `translateX(${off * SPREAD}px) rotate(${off * 5}deg)`, zIndex: 5 - Math.abs(off) }}
      >
        <ProfileCard p={p} />
      </div>
    );
  }
  return (
    <motion.div className="absolute" style={{ x, y, rotate, zIndex: 5 - Math.abs(off) }}>
      <ProfileCard p={p} />
    </motion.div>
  );
}

export default function TheFive({ showLink = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "center 0.5"] });

  return (
    <section data-testid="the-five-section" className="relative py-24 sm:py-36 overflow-hidden bg-[#1a0508] text-ivory-light">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="/story/roses.jpg"
          alt=""
          data-testid="the-five-roses-bg"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1a0508]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0508]/80 via-[#1a0508]/30 to-[#1a0508]/85" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <FadeUp>
            <Eyebrow light>The Five</Eyebrow>
          </FadeUp>
          <h2 className="mt-7 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-ivory-light drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]">
            <MaskedLines
              lines={[
                "Beş kişi.",
                <em key="em" className="italic text-rose-soft">
                  Daha bilinçli bir seçim.
                </em>,
              ]}
            />
          </h2>
          <FadeUp delay={0.2} className="mt-6 text-base font-light text-ivory/85 leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
            The Five, senin için seçilmiş beş kişiden oluşur. Tercihlerin, ilişkiye bakışın ve aradığın uyum bu
            seçimin bir parçasıdır. Çünkü mesele daha fazla insanla tanışmak değil; içlerinden biriyle gerçekten bir
            şey başlayabilmesi.
          </FadeUp>
        </div>
      </div>

      <div ref={ref} className="relative mt-16 sm:mt-20">
        <div className="relative hidden md:flex items-center justify-center h-[540px]" data-testid="the-five-fan">
          {PROFILES.map((p, i) => (
            <FanCard key={p.name} p={p} i={i} progress={scrollYProgress} />
          ))}
        </div>

        <div className="md:hidden -mx-0 px-6 flex gap-4 overflow-x-auto pb-6 snap-x snap-mandatory" data-testid="the-five-row">
          {PROFILES.map((p) => (
            <div key={p.name} className="snap-center">
              <ProfileCard p={p} />
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-4 md:mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-xs text-ivory/60 tracking-wide">Temsili profiller — gerçek kullanıcı verisi değildir.</p>
          {showLink && (
            <Link to="/the-five" data-testid="the-five-more-link" className="link-ghost-light">
              The Five&rsquo;ı keşfet
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
